import { useEffect, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Les deux superpositions de la console — TRANSPOSITION du gabarit d'origine.
 *
 * La console HTML en a DEUX, avec des rôles distincts, et aucune n'était
 * portée :
 *   • `Modale` (`.modal-bg` > `.modal` > `.modal-head` + `.modal-body`) —
 *     centrée, pour SAISIR quelque chose. `.modal-body` porte la mise en forme
 *     des `label` et des champs : hors de lui, un formulaire n'a plus aucun
 *     style.
 *   • `Panneau` (`.overlay` + `.panel`) — tiroir latéral de 500 px qui glisse
 *     depuis la droite, pour CONSULTER une fiche sans quitter la liste.
 *
 * L'ouverture passe par la classe `.open` : les deux conteneurs sont en
 * `display:none` / `translateX(100%)` par défaut, et c'est cette classe qui
 * déclenche la transition. Les monter conditionnellement les ferait
 * apparaître d'un coup, sans glissement.
 *
 * ⚠️ LES DEUX PASSENT PAR UN PORTAIL VERS `document.body`, et ce n'est pas un
 * détail d'implémentation React : c'est la transposition littérale de
 * l'original, où `.overlay`, `.panel` et `.modal-bg` sont trois éléments
 * UNIQUES posés à la fin du `<body>`, hors de toute section.
 *
 * Sans portail, le composant se rend là où il est appelé — à l'intérieur de la
 * carte. Or `section.active > .card` porte l'animation d'entrée `swUp`, et un
 * ancêtre transformé devient le bloc conteneur de tout `position:fixed` qu'il
 * abrite.
 *
 * Le piège est que ça ne s'arrête pas avec l'animation. `swUp` finit sur
 * `transform:none`, mais `animation-fill-mode: both` garde l'animation
 * APPLIQUÉE : la valeur calculée n'est pas le mot-clé `none`, c'est la matrice
 * identité `matrix(1, 0, 0, 1, 0, 0)` — qui reste un transform. La carte est
 * donc bloc conteneur pour toujours.
 *
 * Mesuré dans le navigateur, overlay rendu dans la carte contre overlay rendu
 * dans le `<body>`, fenêtre de 1280 × 720 :
 *
 *     dans la carte : overlay 1280 × 120 posé à 720 px  → hors écran
 *     dans le body  : overlay 1280 × 720 posé à 0       → boîte centrée
 *
 * La modale s'ouvrait donc bel et bien, à la taille de la carte et sous la
 * ligne de flottaison. Un défaut que ni le compte de classes, ni le typage, ni
 * la console du navigateur ne peuvent voir : rien n'est absent, rien n'échoue.
 */

/** Échap ferme — réflexe attendu de toute superposition. */
function useEchap(actif: boolean, surFermer: () => void) {
  useEffect(() => {
    if (!actif) return
    const h = (e: KeyboardEvent) => e.key === 'Escape' && surFermer()
    document.addEventListener('keydown', h)
    return () => document.removeEventListener('keydown', h)
  }, [actif, surFermer])
}

export function Modale({
  titre,
  ouverte,
  surFermer,
  large = false,
  children,
}: {
  titre: string
  ouverte: boolean
  surFermer: () => void
  /** Boîte large (900 px) : grilles, aperçus, éditeurs. */
  large?: boolean
  children: ReactNode
}) {
  useEchap(ouverte, surFermer)
  return createPortal(
    <div
      className={`modal-bg${ouverte ? ' open' : ''}`}
      /* Le clic sur le fond ferme, celui sur la boîte non — sans ce test, un
         clic n'importe où DANS le formulaire fermerait la saisie en cours. */
      onClick={(e) => e.target === e.currentTarget && surFermer()}
    >
      <div className={`modal${large ? ' modal-large' : ''}`} role="dialog" aria-modal="true" aria-label={titre}>
        <div className="modal-head">
          <span>{titre}</span>
          <button className="btn btn-outline btn-sm" onClick={surFermer} aria-label="Fermer">
            ×
          </button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>,
    document.body,
  )
}

export function Panneau({
  titre,
  ouvert,
  surFermer,
  large = false,
  children,
}: {
  titre: string
  ouvert: boolean
  surFermer: () => void
  /** Tiroir large (760 px) pour les fiches denses (dossiers de candidature). */
  large?: boolean
  children: ReactNode
}) {
  useEchap(ouvert, surFermer)
  return createPortal(
    <>
      <div className={`overlay${ouvert ? ' open' : ''}`} onClick={surFermer} />
      <div className={`panel${large ? ' panel-large' : ''}${ouvert ? ' open' : ''}`}>
        <div className="panel-header">
          <h3>{titre}</h3>
          <button className="btn btn-outline btn-sm" onClick={surFermer}>
            × Fermer
          </button>
        </div>
        <div className="panel-body">{children}</div>
      </div>
    </>,
    document.body,
  )
}

/**
 * Ligne d'une fiche en panneau : libellé à gauche, valeur à droite.
 * `.panel-body .row` et `.panel-body .row .lbl` ne s'appliquent QUE dans un
 * panneau — les recopier en styles en ligne ailleurs les ferait diverger.
 */
export function Ligne({ libelle, children }: { libelle: string; children: ReactNode }) {
  return (
    <div className="row">
      <span className="lbl">{libelle}</span>
      <span>{children}</span>
    </div>
  )
}
